export const CHECKOUT_URL =
  'https://decisionclinica.impultienda.ar/checkout/ruta-clinica-de-menopausia';

export const PRODUCT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/tPHxb7utLzQgk8b3Th74EG/DENTISTAS-EN.png';

export const KARITO_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/7901e72b-841b-4162-88b7-4898e0a484ff.jpg';

export const painPoints: string[] = [
  'You’re looking at a ClinCheck and wondering if the plan is actually ready to approve.',
  'You spend too much time reviewing the same case from different angles.',
  'You’re unsure when IPR, expansion, distalization, or other approaches make the most sense.',
  'Attachments are sometimes selected without a clear biomechanical strategy.',
  'Some cases require refinements that you wish you could have anticipated earlier.',
  'You rely heavily on the software or technician to decide if a plan is clinically appropriate.',
  'Complex cases leave you second-guessing your decisions.',
  'You know the theory, but applying it consistently to real cases is the difficult part.',
];

export const beforeItems: string[] = [
  'Uncertainty before approving a ClinCheck',
  'Rechecking the same case repeatedly',
  'Guessing which biomechanics to use',
  'Attachments selected without a clear strategy',
  'Important details overlooked',
  'Unnecessary refinements',
  'Too much dependence on the software',
  'No consistent review process',
];

export const afterItems: string[] = [
  'Follow a structured review process',
  'Analyze each case systematically',
  'Make clinical decisions with confidence',
  'Understand the purpose behind each mechanic',
  'Review critical details before approval',
  'A clearer framework for complex cases',
  'A repeatable planning workflow',
  'Diagnosis to finishing with consistency',
];

export interface Volume {
  number: string;
  title: string;
  intro: string;
  note?: string;
  insideLabel: string;
  inside: string[];
}

export const volumes: Volume[] = [
  {
    number: 'Volume I',
    title: 'Vertical Control Fundamentals',
    intro:
      'Learn how to evaluate the smile, vertical relationships, leveling, and the clinical factors that shape your treatment plan.',
    note: 'Identify important vertical considerations before moving into treatment mechanics.',
    insideLabel: 'Inside',
    inside: [
      'Clinical assessment',
      'Smile analysis',
      'Vertical relationships',
      'Leveling considerations',
      'Common planning mistakes',
      'Visual decision frameworks',
    ],
  },
  {
    number: 'Volume II',
    title: 'The Art of Space: IPR vs. Expansion',
    intro: 'Learn how to approach space creation without relying on guesswork.',
    note:
      'Understand the clinical factors behind IPR, expansion, and other space-management strategies.',
    insideLabel: 'Inside',
    inside: [
      'Space analysis',
      'IPR considerations',
      'Expansion considerations',
      'Proclination risks',
      'Decision frameworks',
      'Clinical examples',
      'Common mistakes',
    ],
  },
  {
    number: 'Volume III',
    title: 'High-Performance Attachments',
    intro:
      'Stop choosing attachments simply because they appear in the software.',
    note:
      'Think about attachments according to the movement and force system you’re trying to achieve.',
    insideLabel: 'Inside',
    inside: [
      'Attachment selection',
      'Movement-specific strategies',
      'Rotation control',
      'Root control',
      'Intrusion and extrusion',
      'Attachment-related errors',
      'Visual biomechanics',
    ],
  },
  {
    number: 'Volume IV',
    title: 'Transverse & Sagittal Protocols',
    intro:
      'Build a clearer framework for managing challenging sagittal and transverse cases with clear aligners.',
    insideLabel: 'Structured approaches to',
    inside: [
      'Class II',
      'Class III',
      'Crossbite',
      'Transverse discrepancies',
      'Distalization',
      'Expansion',
      'Sequencing considerations',
    ],
  },
  {
    number: 'Volume V',
    title: 'Predictability in Root Movement',
    intro: 'Go beyond simply moving crowns.',
    note:
      'Understand what’s involved in controlling torque, root position, and unwanted tipping during aligner treatment.',
    insideLabel: 'Inside',
    inside: [
      'Torque control',
      'Root movement',
      'Tipping',
      'Force systems',
      'Sequencing',
      'Attachments for root control',
      'Common biomechanical mistakes',
    ],
  },
  {
    number: 'Volume VI',
    title: 'Finishing & Refinement Protocol',
    intro:
      'Approach the final stage of treatment with a structured review process — because finishing shouldn’t be an afterthought.',
    insideLabel: 'Inside',
    inside: [
      'Final-stage evaluation',
      'Occlusal assessment',
      'Esthetic finishing',
      'Tracking issues',
      'Refinement decisions',
      'Communication with the technician',
      'Final review',
      'Retention considerations',
    ],
  },
];

export interface Bonus {
  index: string;
  title: string;
  subtitle: string;
  body: string;
  bullets?: string[];
  value: string;
  image: string;
}

export const bonuses: Bonus[] = [
  {
    index: 'Bonus #1',
    title: 'Clinical Communication Command Library',
    subtitle: '80+ ready-to-use clinical instructions',
    body:
      'Stop wasting time trying to explain exactly what you want changed in a treatment plan. A library of professional instructions you can adapt when communicating objectives and modifications to your technician or planning team.',
    value: '$37',
    image:
      'https://cdn.magicpatterns.com/uploads/2TM8Twwh7YRm8cQKtkjZMg/BONUS_1_DENTISTAS-EN.png',
  },
  {
    index: 'Bonus #2',
    title: 'The 12-Point Clinical Approval Checklist',
    subtitle: 'The final 3-minute review before you approve a case',
    body:
      'A practical checklist to review the critical elements before approving a treatment plan. Use it as your final safety net before moving forward.',
    value: '$27',
    image:
      'https://cdn.magicpatterns.com/uploads/4ecv3zhUGP3PZjNDsWify9/BONUS_2_DENTISTAS-EN.png',
  },
  {
    index: 'Bonus #3',
    title: 'Clinical Photography Protocol',
    subtitle: 'Take better clinical records before planning begins',
    body:
      'The key photographic records needed to document and evaluate clear aligner cases consistently — positioning guidance, views, organization, and common mistakes to avoid.',
    value: '$29',
    image:
      'https://cdn.magicpatterns.com/uploads/xcEQqDZMBrJhtgKJnNzW7w/BONUS_3_DENTISTAS-EN.png',
  },
  {
    index: 'Bonus #4',
    title: 'Clinical Patient Progress Dashboard',
    subtitle: 'Track treatment progress visually',
    body:
      'A professional tracking resource to monitor progress, compare clinical findings with the planned movements, and identify potential deviations earlier.',
    value: '$24',
    image:
      'https://cdn.magicpatterns.com/uploads/kqTe6Qei2ACzA5sRXsGYJX/BONUS_4_DENTISTAS-EN.png',
  },
  {
    index: 'Bonus #5',
    title: 'High-Impact Patient Communication Scripts',
    subtitle: 'Know exactly how to explain the most common questions',
    body: 'Ready-to-adapt scripts that make complex clinical concepts easier for patients to understand.',
    bullets: [
      'IPR',
      'Attachments',
      'Treatment duration',
      'Refinements',
      'Compliance',
      'Expectations',
      'Common concerns',
    ],
    value: '$37',
    image:
      'https://cdn.magicpatterns.com/uploads/db5P3W11D4dRrLrAYiTbcd/BONUS_5_DENTISTAS-EN.png',
  },
];

export interface WorkflowStep {
  label: string;
  description: string;
}

export const workflowSteps: WorkflowStep[] = [
  { label: 'Diagnosis', description: 'Understand the case before choosing mechanics.' },
  { label: 'Space', description: 'Evaluate how space should be created and managed.' },
  { label: 'Biomechanics', description: 'Understand the movement you’re asking the aligners to produce.' },
  { label: 'Attachments', description: 'Select attachments according to the movement strategy.' },
  { label: 'Review', description: 'Check the critical details before approving the plan.' },
  { label: 'Finishing', description: 'Evaluate the final stages and decide when refinement is appropriate.' },
];

export const planningQuestions: string[] = [
  'What is the clinical objective?',
  'Where is the space coming from?',
  'Are the movements properly sequenced?',
  'Are the attachments supporting the intended movement?',
  'Is root control being addressed?',
  'What could create a problem later?',
  'What needs to be reviewed before approval?',
];

export interface Audience {
  title: string;
  description: string;
}

export const audiences: Audience[] = [
  {
    title: 'Orthodontists',
    description: 'Who want a structured framework for reviewing and planning clear aligner cases.',
  },
  {
    title: 'General Dentists',
    description: 'Who are incorporating clear aligners and want greater confidence with treatment planning.',
  },
  {
    title: 'Residents & Early-Career Clinicians',
    description: 'Who want to accelerate their understanding with practical frameworks and clinical examples.',
  },
  {
    title: 'Experienced Aligner Providers',
    description: 'Who want to standardize their workflow and create a more consistent planning process.',
  },
];

export interface Resource {
  title: string;
  description: string;
}

export const resources: Resource[] = [
  { title: 'Checklists', description: 'For critical review points.' },
  { title: 'Decision frameworks', description: 'To organize your clinical thinking.' },
  { title: 'Visual diagrams', description: 'To make complex concepts easier to understand.' },
  { title: 'Case-based examples', description: 'To connect principles with real-world scenarios.' },
  { title: 'Ready-to-use resources', description: 'To help you apply the system immediately.' },
];

export interface ValueRow {
  title: string;
  value: string;
  description: string;
}

export const valueRows: ValueRow[] = [
  {
    title: '6-Volume Clinical Library',
    value: '$97',
    description: 'Six specialized volumes covering the planning journey from diagnosis through finishing.',
  },
  {
    title: 'Visual Clinical Decision System',
    value: '$67',
    description: 'Decision maps, visual frameworks, and structured planning resources.',
  },
  {
    title: 'Clinical Protocol Library',
    value: '$47',
    description: 'Practical protocols designed around common planning decisions.',
  },
  {
    title: 'Case & Visual Resource Library',
    value: '$49',
    description: 'Clinical examples, diagrams, and visual learning resources.',
  },
  {
    title: '5 Exclusive Bonuses',
    value: '$154',
    description: 'Additional tools for planning, communication, documentation, and review.',
  },
];

export const includedItems: string[] = [
  '6 Specialized Clinical Volumes',
  '5 Exclusive Professional Bonuses',
  'Visual Decision Frameworks',
  'Clinical Checklists',
  'Case-Based Examples',
  'Practical Planning Protocols',
  'Patient Communication Resources',
  'Instant Digital Access',
];

export interface Testimonial {
  quote: string;
  name: string;
  role: string;
  image: string;
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'The biggest difference for me was having a structured way to review my cases. I no longer feel like I’m just looking at the ClinCheck and hoping everything is correct.',
    name: 'Dr. Michael R.',
    role: 'Orthodontist — United States',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b9fb61ca-df97-4040-8aad-734a115678bc.jpg',
  },
  {
    quote:
      'The protocols are extremely practical. I especially liked the sections on space management and attachments because they give you a framework instead of just more theory.',
    name: 'Dr. Sarah L.',
    role: 'General Dentist — United Kingdom',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9544caab-3f58-40b6-b39f-2519a5d17d7b.jpg',
  },
  {
    quote:
      'The visual approach makes complicated concepts much easier to understand. I can see myself using these resources repeatedly when planning cases.',
    name: 'Dr. Daniel M.',
    role: 'Orthodontist — Australia',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9c4d7ca9-eab3-4203-951d-e9822d71121b.jpg',
  },
  {
    quote:
      'What I liked most is that it’s organized around actual clinical decisions. It’s much easier to apply than going through another traditional textbook.',
    name: 'Dr. James K.',
    role: 'Dentist — Canada',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/3c808d5e-2cfa-4102-8c35-b712af9d3b97.jpg',
  },
];

export interface Faq {
  question: string;
  answer: string;
}

export const faqs: Faq[] = [
  {
    question: 'How will I receive the System?',
    answer:
      'You’ll receive immediate digital access after completing your purchase. The materials can be accessed and downloaded from your device.',
  },
  {
    question: 'Is this a physical book?',
    answer:
      'No. The entire System is delivered digitally, so you can access your materials whenever you need them.',
  },
  {
    question: 'Is this a one-time payment?',
    answer:
      'Yes. There is no monthly subscription. You pay once and receive lifetime digital access to the product you purchased.',
  },
  {
    question: 'Does this work with different clear aligner systems?',
    answer:
      'The educational framework focuses on clinical planning principles, decision-making, biomechanics, and review processes rather than being limited to one specific aligner brand.',
  },
  {
    question: 'Is this only for orthodontists?',
    answer:
      'No. It is also useful for general dentists, residents, and clinicians incorporating clear aligner treatment into their practices.',
  },
  {
    question: 'Will this replace my clinical training or professional judgment?',
    answer:
      'No. This is an educational resource designed to organize clinical concepts and provide practical frameworks. Treatment decisions should always be based on your professional judgment, patient-specific findings, applicable standards of care, and appropriate clinical training.',
  },
];

export interface PurchaseNotice {
  name: string;
  country: string;
}

export const recentPurchases: PurchaseNotice[] = [
  { name: 'Dr. Carolina Méndez', country: 'Mexico' },
  { name: 'Dr. Javier Restrepo', country: 'Colombia' },
  { name: 'Dr. Lucía Fernández', country: 'Spain' },
  { name: 'Dr. Andrés Villalobos', country: 'Costa Rica' },
  { name: 'Dr. Mariana Quispe', country: 'Peru' },
  { name: 'Dr. Rodrigo Salinas', country: 'Chile' },
  { name: 'Dr. Valentina Duarte', country: 'Argentina' },
  { name: 'Dr. Esteban Morales', country: 'Ecuador' },
  { name: 'Dr. Paula Gutiérrez', country: 'Uruguay' },
  { name: 'Dr. Fernando Ríos', country: 'Guatemala' },
  { name: 'Dr. Daniela Castro', country: 'Panama' },
  { name: 'Dr. Ignacio Herrera', country: 'Dominican Republic' },
];
